// Tabs outlive deploys: an SPA loaded yesterday keeps running yesterday's
// code (e.g. job polling that has since been fixed) until it is reloaded.
// Every build gets a new hashed entry script, so comparing the one this page
// runs with the one the server serves now tells us a newer build is out.

const CHECK_EVERY_MS = 10 * 60_000;

const entryOf = (html: string) => /<script[^>]+type="module"[^>]+src="([^"]+)"/.exec(html)?.[1] ?? null;

function runningEntry(): string | null {
  const el = document.querySelector<HTMLScriptElement>('script[type="module"][src]');
  return el ? new URL(el.src, location.href).pathname : null;
}

export async function isNewBuildOut(fetchFn: typeof fetch = fetch): Promise<boolean> {
  const current = runningEntry();
  // The dev server has no hashed entry; nothing to compare.
  if (!current || !current.includes('/assets/')) return false;
  try {
    const res = await fetchFn('/', { cache: 'no-store', credentials: 'same-origin' });
    if (!res.ok) return false;
    const served = entryOf(await res.text());
    return !!served && new URL(served, location.href).pathname !== current;
  } catch {
    return false;
  }
}

// Calls `onNew` once a newer build is detected: on an interval and whenever
// the tab comes back into view. Returns a stop function.
export function watchForNewBuild(onNew: () => void): () => void {
  let done = false;
  const check = () => {
    if (done || document.hidden) return;
    void isNewBuildOut().then((yes) => {
      if (yes && !done) {
        done = true;
        onNew();
      }
    });
  };
  const id = setInterval(check, CHECK_EVERY_MS);
  document.addEventListener('visibilitychange', check);
  return () => {
    done = true;
    clearInterval(id);
    document.removeEventListener('visibilitychange', check);
  };
}
